import { useCallback, useEffect, useState } from "react";
import { BellPlus, Pencil, Send, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import AlertFormDialog from "@/components/AlertFormDialog";
import { errMsg, useApi } from "@/lib/api";
import { DIRECTION_LABEL, dt, eur, KIND_LABEL, STATUS_STYLE, unitDigits } from "@/lib/format";

const StatusBadge = ({ a }) =>
  !a.active ? <span className="rounded border border-slate-200 px-2 py-0.5 text-[11px] text-slate-500">pausiert</span>
  : a.triggered ? <span className="rounded border border-orange-500/40 bg-orange-500/10 px-2 py-0.5 text-[11px] text-orange-700">ausgelöst</span>
  : <span className="rounded border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[11px] text-emerald-600">wartet</span>;

const DeleteBtn = ({ onConfirm, id }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild><Button variant="ghost" size="icon" data-testid={`btn-delete-alert-${id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button></AlertDialogTrigger>
    <AlertDialogContent className="border-slate-200 bg-white">
      <AlertDialogHeader><AlertDialogTitle>Alarm löschen?</AlertDialogTitle></AlertDialogHeader>
      <AlertDialogFooter><AlertDialogCancel>Abbrechen</AlertDialogCancel><AlertDialogAction onClick={onConfirm} data-testid="btn-confirm-delete-alert">Löschen</AlertDialogAction></AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const LogTable = ({ logs }) => (
  <div className="panel overflow-x-auto p-5" data-testid="table-alert-logs">
    <h3 className="mb-4 text-base font-semibold">Versandprotokoll</h3>
    {logs.length === 0 ? <p className="text-sm text-slate-500">Noch keine Reports.</p> : (
      <table className="w-full min-w-[720px] text-sm">
        <thead className="text-left text-xs uppercase tracking-wider text-slate-500"><tr><th className="pb-2">Zeit</th><th className="pb-2">Art</th><th className="pb-2">Sorte</th><th className="pb-2 text-right">Kurs</th><th className="pb-2 pl-6">Empfänger</th><th className="pb-2">Status</th></tr></thead>
        <tbody>
          {logs.map((l) => (
            <tr key={l.id} className="border-t border-slate-200">
              <td className="py-2.5 text-slate-500">{dt(l.ts)}</td>
              <td className="py-2.5">{KIND_LABEL[l.kind] || l.kind}</td>
              <td className="py-2.5">{l.grade_name}</td>
              <td className="num py-2.5 text-right">{eur(l.price, unitDigits(l.unit))}/{l.unit || "kg"}</td>
              <td className="max-w-[220px] truncate py-2.5 pl-6 text-slate-500">{l.recipients.join(", ")}</td>
              <td className="py-2.5"><span className={`rounded border px-1.5 py-0.5 text-[11px] ${STATUS_STYLE[l.status]}`} title={l.error}>{l.status}</span>{l.error && <div className="mt-1 text-[11px] text-slate-500">{l.error}</div>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export default function Alarme() {
  const api = useApi();
  const [alerts, setAlerts] = useState(null);
  const [grades, setGrades] = useState([]);
  const [boerse, setBoerse] = useState(null);
  const [metals, setMetals] = useState([]);
  const [logs, setLogs] = useState([]);
  const [form, setForm] = useState(null);
  const [sending, setSending] = useState(null);

  const load = useCallback(async () => {
    const [a, g, l, p] = await Promise.all([api.get("/alerts"), api.get("/grades"), api.get("/alert-logs"), api.get("/prices/current").catch(() => ({ data: null }))]);
    setAlerts(a.data); setGrades(g.data); setLogs(l.data); setBoerse(p.data?.eur_per_kg);
    api.get("/metals").then((r) => setMetals(r.data)).catch(() => {});
  }, [api]);
  useEffect(() => { load(); }, [load]);


  const send = async (a) => {
    setSending(a.id);
    try {
      const r = await api.post(`/alerts/${a.id}/send`);
      r.data.status === "gesendet" ? toast.success("Report versendet") : toast.warning(`Report ${r.data.status}: ${r.data.error}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setSending(null);
  };
  const remove = async (a) => { try { await api.delete(`/alerts/${a.id}`); toast.success("Alarm gelöscht"); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (!alerts) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>;

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Preisalarme</h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-500">Der Kurs wird alle 15 Minuten geprüft. Wird der Schwellenwert erreicht, geht ein Entwicklungsreport an die Empfänger. Erst wenn der Kurs wieder zurückfällt, wird der Alarm erneut scharf geschaltet.</p>
        </div>
        <Button onClick={() => setForm({})} data-testid="btn-create-price-alert"><BellPlus className="mr-2 h-4 w-4" /> Neuer Alarm</Button>
      </div>
      <div className="panel overflow-x-auto p-5">
        {alerts.length === 0 ? <p className="py-6 text-center text-sm text-slate-500" data-testid="alerts-empty">Noch keine Alarme angelegt.</p> : (
          <table className="w-full min-w-[820px] text-sm" data-testid="table-alerts">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500"><tr><th className="pb-3">Alarm</th><th className="pb-3">Bedingung</th><th className="pb-3 text-right">Aktuell</th><th className="pb-3 pl-6">Empfänger</th><th className="pb-3">Status</th><th className="pb-3 text-right">Aktionen</th></tr></thead>
            <tbody>
              {alerts.map((a) => (
                <tr key={a.id} className="border-t border-slate-200" data-testid={`row-alert-${a.id}`}>
                  <td className="py-3"><div className="font-medium">{a.name || a.target_name}</div><div className="text-xs text-slate-500">{a.target_name}{a.last_triggered_at && ` · zuletzt ${dt(a.last_triggered_at)}`}</div></td>
                  <td className="num py-3">{DIRECTION_LABEL[a.direction]} {eur(a.threshold)}/{a.unit}</td>
                  <td className="num py-3 text-right text-orange-700">{eur(a.current_price, unitDigits(a.unit))}/{a.unit}</td>
                  <td className="max-w-[220px] truncate py-3 pl-6 text-slate-500">{a.recipients.join(", ")}</td>
                  <td className="py-3"><StatusBadge a={a} /></td>
                  <td className="py-3 text-right whitespace-nowrap">
                    <Button variant="ghost" size="icon" onClick={() => send(a)} disabled={sending === a.id} title="Report jetzt senden" data-testid={`btn-trigger-test-report-${a.id}`}>{sending === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</Button>
                    <Button variant="ghost" size="icon" onClick={() => setForm(a)} data-testid={`btn-edit-alert-${a.id}`}><Pencil className="h-4 w-4" /></Button>
                    <DeleteBtn id={a.id} onConfirm={() => remove(a)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <LogTable logs={logs} />
      {form && <AlertFormDialog alert={form.id ? form : null} grades={grades} boerse={boerse} metals={metals} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </div>
  );
}
